import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect(true);
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Keeps the bubble in sync with the real cart.
   * GZ QA fix (GZQA-001, 2026-10-16): the stock version preferred a <10 s old
   * sessionStorage value over the server-rendered count, and never corrected a
   * back/forward-cache restore older than 10 s. A fresh document now trusts the
   * server-rendered count; when the page may be stale (back/forward, bfcache) or the
   * stored value disagrees with it, Shopify's /cart.js decides.
   * @param {boolean} [restored=false] - True when the page came from the back/forward cache.
   */
  ensureCartBubbleIsCorrect = async (restored = false) => {
    if (!this.refs.cartBubbleCount) return;

    const visibleCount = this.refs.cartBubbleCount.textContent?.trim() || '0';
    const navigationType = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    )?.type;

    let storedValue = null;
    try {
      storedValue = JSON.parse(sessionStorage.getItem('cart-count') ?? 'null')?.value ?? null;
    } catch (_) {
      // no-op
    }

    const mayBeStale = restored || navigationType === 'back_forward';
    if (!mayBeStale && (storedValue === null || storedValue === visibleCount)) {
      this.#storeCount(visibleCount);
      return;
    }

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) return;
      const { item_count: itemCount } = await response.json();
      if (typeof itemCount !== 'number') return;

      // The bubble is blank at 100+ items, so treat any 100+ count as matching a blank bubble.
      const shownCount = itemCount < 100 ? String(itemCount) : '';
      if (shownCount === (this.refs.cartBubbleCount.textContent?.trim() ?? '')) {
        this.#storeCount(visibleCount);
      } else {
        this.renderCartBubble(itemCount, false);
        for (const cartItems of document.querySelectorAll('cart-items-component')) {
          /** @type {any} */ (cartItems).refresh?.()?.catch?.(() => {});
        }
      }
    } catch (_) {
      // Network failure: keep the server-rendered count.
    }
  };

  /**
   * @param {string} value
   */
  #storeCount(value) {
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value, timestamp: Date.now() }));
    } catch (_) {
      // no-op
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
